import React from 'react';
import {
    CompetitiveRoundSummaries,
    CompetitiveRoundSummariesDeathEventInner,
    CompetitiveRoundSummariesDeathEventInnerKillerLocation,
    CompetitiveRoundSummariesSmokeAbilitiesInner,
    CompetitiveRoundSummariesUtilityAbilitiesInner,
    Maps
} from "../../reckon-api";
import Box from "@mui/material/Box";
import {Circle, ImageOverlay, MapContainer, Popup} from "react-leaflet";
import {CRS} from "leaflet";
import {useEffect, useRef, useState} from "react";
import 'leaflet/dist/leaflet.css';
import "./Map.css";
import {CircularProgress} from "@mui/material";
import {defender_color} from "../../theme";
import UtilityAbilityOnMap from "./Markers/UtilityAbilityOnMap";
import PlayerOnMap from "./Markers/PlayerOnMap";
import {formatDuration} from "../../utils";
import {ValorantApi} from "../../valorant-api/api";
import {MapData} from "../../valorant-api/types";

// Extended types that include agent and side information
export type SmokeAbilityWithAgent = CompetitiveRoundSummariesSmokeAbilitiesInner & { agent: string; side: string };
export type UtilityAbilityWithAgent = CompetitiveRoundSummariesUtilityAbilitiesInner & { agent: string; side: string };
export type KillWithAgent = CompetitiveRoundSummariesDeathEventInner & { killer_agent: string; side: string };

export function positionToMapBounds(position: {x:number, y:number}, map: Maps){
    if(!['abyss'].includes(map.id)){
        return {
            y: (position.y - map.min_y) / (map.max_y - map.min_y),
            x: (position.x - map.min_x) / (map.max_x - map.min_x),
        }
    }else{
        return {
            x: (position.x * (map.x_multiplier as number)) + (map.x_scalar_to_add as number),
            y: (position.y * (map.y_multiplier as number *-1)) + (map.y_scalar_to_add as number),
        }
    }
}
export const normalizeVector = (vector: { x: number; y: number; z: number }) => {
  const magnitude = Math.sqrt(vector.x ** 2 + vector.y ** 2 + vector.z ** 2);
  return magnitude > 0
    ? { x: vector.x / magnitude, y: vector.y / magnitude, z: vector.z / magnitude }
    : { x: 0, y: 0, z: 0 };
};

export const scaleVector = (vector: { x: number; y: number; z: number }, scale: number) => ({
  x: vector.x * scale,
  y: vector.y * scale,
  z: vector.z * scale,
});

// SpecifiedPosition matches the shared location shape used by positions/casts
export type SpecifiedPosition = CompetitiveRoundSummariesDeathEventInnerKillerLocation;

export function MapRender(props: { map: Maps;
    id: string; children?: React.ReactNode;
    playerPositions?: {round: CompetitiveRoundSummaries, position?: CompetitiveRoundSummariesDeathEventInnerKillerLocation }[]
    smokes: SmokeAbilityWithAgent[]
    kills: KillWithAgent[]
    utils: UtilityAbilityWithAgent[]
    spikePosition?: {x: number, y: number}
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState(null as number | null);
    const [, setMapData] = useState(undefined as MapData|undefined);

    useEffect(() => {
        ValorantApi.getMap(props.map.uuid as string)
          .then((response) => {
            setMapData(response.data as MapData);
          })
    }, [props.map]);

    useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    if (entry.contentRect) {
                        const size = Math.floor(Math.min(entry.contentRect.width, entry.contentRect.height));
                        setContainerSize(size+1);
                    }
                }
            });

            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

    const map_url = `https://res.cloudinary.com/xenesis/image/upload/c_scale,h_${containerSize},w_${containerSize}/valAssets/${props.map ? props.map.id : props.id}.png`;
    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {containerSize ? <MapContainer
                center={[containerSize / 2, containerSize / 2]}
                scrollWheelZoom={false}
                zoom={0}
                dragging={false}
                minZoom={0}
                maxZoom={0}
                attributionControl={false}
                zoomControl={false}
                doubleClickZoom={false}
                crs={CRS.Simple}
                bounds={[
                    [0, 0],
                    [containerSize, containerSize],
                ]}
            >
                <ImageOverlay
                    url={map_url}
                    bounds={[
                        [0, 0],
                        [containerSize, containerSize],
                    ]}
                />
                {props.smokes.map((smoke, index) => {
                    const position_normalized = positionToMapBounds(smoke.landing_point as {x: number, y:number}, props.map);
                    position_normalized.x = position_normalized.x * containerSize;
                    position_normalized.y = position_normalized.y * containerSize;
                    const smokeTime = smoke.cast_position?.time_in_round ?? 0;

                    return <Circle
                        key={index + 'smoke'}
                        center={[position_normalized.x, position_normalized.y]}
                        radius={25}
                        pathOptions={{ color: defender_color, fillColor: '#000000', fillOpacity: 0.5 }}
                    >
                        <Popup>
                            <span>{smoke.slot}-{formatDuration(smokeTime)}</span>
                        </Popup>
                    </Circle>
                })}

                {props.utils.map((util, index) => <UtilityAbilityOnMap key={index} util={util} containerSize={containerSize} map={props.map}/>)}

                {props.playerPositions ? props.playerPositions.map((prs, idx) =>
                <PlayerOnMap key={idx} map={props.map} containerSize={containerSize} prs={prs}/>) : <></>
                }

                {/* Spike marker when planted */}
                {props.spikePosition && (() => {
                    const spikeNormalized = positionToMapBounds(props.spikePosition, props.map);
                    const spikeX = spikeNormalized.x * containerSize;
                    const spikeY = spikeNormalized.y * containerSize;
                    return (
                        <>
                            {/* Outer glow ring */}
                            <Circle
                                center={[spikeX, spikeY]}
                                radius={20}
                                pathOptions={{ 
                                    color: '#22C55E', 
                                    fillColor: '#22C55E', 
                                    fillOpacity: 0.2,
                                    weight: 2,
                                    dashArray: '4, 4'
                                }}
                            />
                            {/* Inner spike marker */}
                            <Circle
                                center={[spikeX, spikeY]}
                                radius={10}
                                pathOptions={{ 
                                    color: '#16A34A', 
                                    fillColor: '#22C55E', 
                                    fillOpacity: 0.9,
                                    weight: 3
                                }}
                            >
                                <Popup>
                                    <span>💣 SPIKE PLANTED</span>
                                </Popup>
                            </Circle>
                        </>
                    );
                })()}
            </MapContainer> : <CircularProgress/>}
        </Box>
    );
}
